import type {
  ShopContext,
  CartItem,
  Product,
  ProductVariant,
  Session,
  Checkout,
  Confirmation,
  Params,
  CollectionResult,
  Result,
  Campaign,
  CollectionSearchResult,
  QueryParams,
  AttributeParams,
  PaginationParams,
  Stock,
  Sales,
  Collection,
  InventoryContext,
  CustomBlogSearchData,
  CustomBlogSearchParameters
} from './types';

import type { Locale } from './types/general';

export * from './types';

export abstract class Adapter<C extends Record<string, unknown> = {}> {
  public constructor(protected config: C) {}
}

export abstract class ShopAdapter<C extends Record<string, unknown>> extends Adapter<C> {
  // Session
  abstract createSession(params: unknown): Promise<Session>;
  abstract getSession(context: ShopContext): Promise<Session | undefined>;
  // Cart
  abstract addCartItem(newItem: CartItem, context: ShopContext): Promise<Session>;
  abstract updateCartItem(cartItem: CartItem, context: ShopContext): Promise<Session>;
  abstract removeCartItem(cartItem: Pick<CartItem, 'id' | 'sku'>, context: ShopContext): Promise<Session>;
  // Checkout
  abstract checkout(data: unknown, context: ShopContext): Promise<Checkout | undefined>;
  abstract confirmation(data: unknown, context: ShopContext): Promise<Confirmation | undefined>;
  // Discount and campaigns
  abstract addDiscountCode(code: unknown, context: ShopContext): Promise<Session | undefined>;
  abstract removeDiscountCode(code: unknown, context: ShopContext): Promise<Session | undefined>;
}

export abstract class CommerceAdapter<C extends Record<string, unknown> = {}> extends Adapter<C> {
  abstract getProducts(params: AttributeParams, locale: Locale, context: unknown): Promise<CollectionResult<Product>>;
  abstract getProductVariants(params: AttributeParams, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract getProduct(handle: string, locale: Locale, context: unknown): Promise<Result<Product>>;
  abstract getProductVariant(sku: string, locale: Locale, context: unknown): Promise<Result<ProductVariant>>;
  abstract getActiveCampaigns(): Promise<Campaign[]>;
  abstract search(params: QueryParams<CustomBlogSearchParameters>, locale: Locale, context: unknown): Promise<CollectionSearchResult<ProductVariant, CustomBlogSearchData>>;
  abstract getPopularSearches(params: PaginationParams, locale: Locale, context: unknown): Promise<CollectionResult<string>>;
  abstract getRecommendation(params: Params, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract getRecommendationDerivedFrom(skus: string[], params: Params, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract getRelatedProducts(sku: string, params: Params, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract getPopularProducts(params: Params, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract getRecentlyInteractedProducts(params: Params, locale: Locale, context: unknown): Promise<CollectionResult<ProductVariant>>;
  abstract events(event: unknown, locale: Locale, context: unknown): Promise<void>;
}

export abstract class InventoryAdapter<C extends Record<string, unknown>> extends Adapter<C> {
  abstract getStock(params: unknown, context: InventoryContext): Promise<Stock>;
  abstract get30DaySales(params: unknown, context: InventoryContext): Promise<Partial<Sales>>;
};

export abstract class ContentAdapter<C extends Record<string, unknown> = {}> extends Adapter<C> {
  abstract getCollection(params: { fullSlug: string, locale: string }): Promise<Collection | undefined>;
}

export class AgapiAdapterError<T> extends Error {
  constructor(message: string, public status: number, public data?: T) {
    super(message);
  }
}
